import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, BookOpen, Stack } from "@phosphor-icons/react";
import { BLOCK_TYPES, fmtCap } from "./blockMeta";
import { useAuth } from "@/contexts/AuthContext";
import { getUnit } from "@/lib/weight";
import { formatStoredWeight } from "@/components/app/WeightInput";

export default function BlockTypePicker({ open, onOpenChange, onPick, onPickWod, onPickTemplate }) {
  const { user } = useAuth();
  const userUnit = getUnit(user);
  const [showWods, setShowWods] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [wods, setWods] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [wodQ, setWodQ] = useState("");
  const [tplQ, setTplQ] = useState("");
  const [wodCat, setWodCat] = useState("all");

  useEffect(() => {
    if (showWods && wods.length === 0) {
      api.get("/wod-library").then((r) => setWods(r.data)).catch(() => {});
    }
  }, [showWods, wods.length]);

  useEffect(() => {
    if (showTemplates && templates.length === 0) {
      api.get("/block-templates").then((r) => setTemplates(r.data)).catch(() => {});
    }
  }, [showTemplates, templates.length]);

  const cats = ["all", ...Array.from(new Set(wods.map((w) => w.category)))];
  const filtered = wods.filter((w) => {
    if (wodCat !== "all" && w.category !== wodCat) return false;
    if (wodQ && !w.name.toLowerCase().includes(wodQ.toLowerCase())) return false;
    return true;
  });

  const filteredTemplates = templates.filter((t) => !tplQ || t.title.toLowerCase().includes(tplQ.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setShowWods(false); setShowTemplates(false); } }}>
      <DialogContent className="rounded-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {showWods ? "Benchmark WOD library" : showTemplates ? "Your saved workouts" : "Add section"}
          </DialogTitle>
          <DialogDescription>
            {showWods
              ? "Tap a benchmark to drop it into the day — movements, reps and rep scheme included."
              : showTemplates
                ? "Insert one of your saved workout templates into this day."
                : "Pick what kind of work this is. Or insert a benchmark or saved workout with one tap."}
          </DialogDescription>
        </DialogHeader>

        {!showWods && !showTemplates && (
          <>
            <button
              type="button"
              onClick={() => setShowWods(true)}
              className="w-full mt-1 p-4 rounded-xl bg-gradient-to-br from-terracotta to-terracotta-hover text-white text-left hover:from-terracotta-hover hover:to-terracotta transition-colors"
              data-testid="open-wod-library"
            >
              <div className="flex items-center gap-2 font-heading font-semibold">
                <BookOpen size={18} weight="fill" /> Insert a benchmark WOD
              </div>
              <div className="text-xs mt-1 text-white/85">
                Fran · Cindy · Helen · Murph · Grace · Diane · DT · Annie · 18 more — pre-loaded.
              </div>
            </button>

            <button
              type="button"
              onClick={() => setShowTemplates(true)}
              className="w-full mt-2 p-4 rounded-xl border-2 border-moss bg-sage/30 text-left hover:bg-sage/50 transition-colors"
              data-testid="open-template-library"
            >
              <div className="flex items-center gap-2 font-heading font-semibold text-moss">
                <Stack size={18} weight="fill" /> Insert from your saved workouts
              </div>
              <div className="text-xs mt-1 text-moss-hover">
                Templates you've saved in your Library — tweak weights afterwards.
              </div>
            </button>

            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {Object.entries(BLOCK_TYPES).map(([key, meta]) => {
                const Icon = meta.icon;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => onPick(key)}
                    className="text-left p-4 rounded-xl border border-line hover:border-moss hover:bg-sage/30 transition-colors"
                    data-testid={`block-type-${key}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`h-8 w-8 rounded-lg grid place-items-center ${meta.tint}`}>
                        <Icon size={16} weight="bold" />
                      </div>
                      <div className="font-heading font-semibold">{meta.label}</div>
                    </div>
                    <div className="mt-2 text-xs text-ink-2 leading-relaxed">{meta.desc}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {showWods && (
          <>
            <div className="mt-2">
              <Input
                placeholder="Search Fran, Cindy, Murph…"
                value={wodQ}
                onChange={(e) => setWodQ(e.target.value)}
                className="h-11 rounded-xl"
                data-testid="wod-search"
              />
              <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
                {cats.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setWodCat(c)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium uppercase tracking-[0.15em] transition-colors ${
                      wodCat === c ? "bg-moss text-white" : "bg-muted2 text-ink-2 hover:bg-sage"
                    }`}
                    data-testid={`wod-cat-${c}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-3 space-y-2">
              {filtered.length === 0 && (
                <div className="text-sm text-ink-3 text-center py-6">No matches.</div>
              )}
              {filtered.map((w) => {
                const sc = w.scheme_config || {};
                const subline = [
                  sc.rep_scheme && sc.rep_scheme.join("-"),
                  sc.rounds && `${sc.rounds} rds`,
                  sc.time_cap_seconds && `cap ${fmtCap(sc.time_cap_seconds)}`,
                ].filter(Boolean).join(" · ");
                return (
                  <button
                    key={w.slug}
                    type="button"
                    onClick={() => onPickWod(w)}
                    className="w-full text-left p-3 rounded-xl border border-line hover:border-terracotta hover:bg-terracotta/5 transition-colors"
                    data-testid={`wod-item-${w.slug}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-base truncate">{w.name}</div>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-ink-3 mt-0.5">
                          {w.category} · {w.type} {subline && `· ${subline}`}
                        </div>
                        <div className="text-xs text-ink-2 mt-1.5 line-clamp-2">{w.description}</div>
                      </div>
                      <Plus size={16} className="text-terracotta mt-1 shrink-0" />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 font-data text-[11px]">
                      {(w.movements || []).map((m, idx) => (
                        <span key={idx} className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">
                          {m.name}{m.weight ? ` @ ${formatStoredWeight(m.weight, m.weight_unit || userUnit)}` : ""}{m.reps ? ` ×${m.reps}` : ""}{m.distance_meters ? ` ${m.distance_meters}m` : ""}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="mt-4">
              <Button type="button" variant="ghost" onClick={() => setShowWods(false)} className="w-full rounded-xl">
                ← Back to all section types
              </Button>
            </div>
          </>
        )}

        {showTemplates && (
          <>
            <Input
              placeholder="Search saved workouts…"
              value={tplQ}
              onChange={(e) => setTplQ(e.target.value)}
              className="h-11 rounded-xl"
              data-testid="template-picker-search"
            />
            <div className="mt-3 space-y-2 max-h-[55vh] overflow-y-auto">
              {filteredTemplates.length === 0 && (
                <div className="text-sm text-ink-3 text-center py-6">
                  No saved workouts yet. Head to the Library to save a benchmark or build one.
                </div>
              )}
              {filteredTemplates.map((t) => {
                const sc = t.scheme_config || {};
                const subline = [
                  sc.rep_scheme && sc.rep_scheme.join("-"),
                  sc.rounds && `${sc.rounds} rds`,
                  sc.time_cap_seconds && `cap ${fmtCap(sc.time_cap_seconds)}`,
                ].filter(Boolean).join(" · ");
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => onPickTemplate(t)}
                    className="w-full text-left p-3 rounded-xl border border-line hover:border-moss hover:bg-sage/30 transition-colors"
                    data-testid={`template-item-${t.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-heading font-semibold text-base truncate">{t.title}</div>
                        <div className="text-[11px] uppercase tracking-[0.15em] text-ink-3 mt-0.5">
                          {t.type}{subline && ` · ${subline}`}
                        </div>
                      </div>
                      <Plus size={16} className="text-moss mt-1 shrink-0" />
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 font-data text-[11px]">
                      {(t.movements || []).map((m, idx) => (
                        <span key={m.id || idx} className="px-2 py-0.5 rounded-md bg-muted2 text-ink-2">
                          {m.name}{m.weight ? ` @ ${formatStoredWeight(m.weight, m.weight_unit || userUnit)}` : ""}{m.reps ? ` ×${m.reps}` : ""}
                        </span>
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <Button type="button" variant="ghost" onClick={() => setShowTemplates(false)} className="w-full rounded-xl">
                ← Back to all section types
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
